/**
 * Front-end port of the backend's reservation pricing, used to show guests an **estimate**
 * (`POST /api/reservations/quote` needs a signed-in client: LOGIC.md F2).
 *
 * Source of truth (CarRent-back, read-only):
 *   controllers/reservationController.js  getReservationQuote, resolveReservationPricing,
 *                                         resolveCarStayPricing, resolveBikeStayPricing,
 *                                         resolveDeliveryFee, pickLongStayDiscount
 *   services/pricingService.js            computeStayPrice, buildCampaignIntervals, splitReservationCommission
 *   utiles/rentalDays.js                  rentalDaysForPricing
 *
 * Every function is pure: the database reads of the backend (platform config, matrix entry,
 * active campaign, agency delivery settings) are passed in as `PricingContext`.
 * `pricing.parity.test.ts` runs the backend's own code on the same inputs and checks the results match.
 */
import { calendarNights, rentalDaysForPricing } from './rentalDays';

export interface PriceTier {
  days: number;
  pricePerDay: number;
  basePricePerDay?: number | null;
}

/** The vehicle document fields the pricing reads. */
export interface PricingVehicle {
  type: 'car' | 'bike';
  /** Client price per day (commission included). */
  pricePerDay: number;
  /** Agency net per day. */
  basePricePerDay: number;
  /** Bikes: price per day from N days. */
  pricingTiers?: PriceTier[];
  minRentalDays?: number | null;
}

/** A PricingMatrix entry, already resolved for the vehicle's year (cars). */
export interface PricingMatrixEntry {
  pricePerDay: number;
  basePricePerDay: number;
  tiers?: PriceTier[];
  minRentalDays?: number | null;
  deliveryFee?: number | null;
}

/** The platform campaign active on the start date, if any (client prices only). */
export interface PricingCampaign {
  pricePerDay: number;
  pricingTiers?: { days: number; pricePerDay: number }[];
}

export interface LongStayConfig {
  enabled: boolean;
  weeklyPct: number;
  monthlyPct: number;
}

export interface PricingContext {
  /** AppParam `commissionRate` (0.15 by default). */
  commissionRate: number;
  /** AppParam `longStayDiscounts` (platform-wide). */
  longStay: LongStayConfig;
  /** PlatformConfig `minRentalDays` (floor over the vehicle's own minimum). */
  platformMinRentalDays: number;
  /** PlatformConfig `freeCityDelivery`. */
  freeCityDelivery?: boolean;
  matrix?: PricingMatrixEntry | null;
  campaign?: PricingCampaign | null;
  /** Agency delivery settings (`deliveryEnabled`, `deliveryPrice`). */
  agencyDelivery?: { enabled: boolean; price: number } | null;
}

export interface PricingRequest {
  /** `yyyy-MM-dd` */
  startDate: string;
  returnDate: string;
  /** `HH:mm` */
  pickupTime: string;
  returnTime: string;
  rentalType: 'pickup' | 'delivery';
}

export interface StayPrice {
  totalClient: number;
  totalNet: number;
}

export interface ReservationPricing {
  /** Billable days (calendar nights + the 2 h extra day). */
  nights: number;
  effectiveMinDays: number;
  /** Rental total after the long-stay discount, before delivery. */
  rentalTotal: number;
  /** Rental total before the long-stay discount. */
  rentalBeforeDiscount: number;
  longStayPct: number;
  deliveryFee: number;
  netBaseSum: number;
}

export interface ReservationQuote extends ReservationPricing {
  totalPrice: number;
  platformFee: number;
  agencyRevenue: number;
}

/** Refusals, with the backend's reasons. */
export class PricingError extends Error {
  constructor(
    message: string,
    readonly code: 'invalid_dates' | 'invalid_times' | 'invalid_rental_type' | 'min_days',
    readonly minRentalDays?: number,
  ) {
    super(message);
    this.name = 'PricingError';
  }
}

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
/** `+(n).toFixed(2)`, as the backend writes it. */
const fixed2 = (n: number) => Number(n.toFixed(2));

/** reservationController.pickLongStayDiscount */
export function pickLongStayDiscount(days: number, cfg: Partial<LongStayConfig> | null | undefined): number {
  if (!cfg || cfg.enabled === false) return 0;
  const weeklyPct = Number(cfg.weeklyPct || 0);
  const monthlyPct = Number(cfg.monthlyPct || 0);
  if (days >= 28 && monthlyPct > 0) return monthlyPct;
  if (days >= 7 && weeklyPct > 0) return weeklyPct;
  return 0;
}

/**
 * pricingService.computeStayPrice with the intervals of buildCampaignIntervals:
 * the campaign's net price covers the calendar nights [start, end); any night beyond
 * (the extra day) falls back to the vehicle's own net price.
 */
function campaignStayPrice(
  req: PricingRequest,
  campaign: PricingCampaign,
  carBaseNet: number,
  nights: number,
  rate: number,
): { totalClient: number; nightsNet: number[] } {
  const campaignNet = campaign.pricePerDay / (1 + rate);
  const inCampaign = calendarNights(req.startDate, req.returnDate);
  const nightsNet: number[] = [];
  let totalClient = 0;
  for (let i = 0; i < nights; i++) {
    const net = i < inCampaign ? campaignNet : carBaseNet;
    nightsNet.push(net);
    totalClient += Math.round(net * (1 + rate) * 100) / 100;
  }
  return { totalClient, nightsNet };
}

/** reservationController.resolveCarStayPricing: campaign → matrix → legacy. */
function carStayPrice(vehicle: PricingVehicle, ctx: PricingContext, req: PricingRequest, nights: number): StayPrice {
  const rate = ctx.commissionRate;
  if (ctx.campaign) {
    const stay = campaignStayPrice(req, ctx.campaign, Number(vehicle.basePricePerDay || 0), nights, rate);
    // computeStayPrice returns no totalNet: resolveReservationPricing sums the nightly nets.
    return { totalClient: stay.totalClient, totalNet: stay.nightsNet.reduce((a, n) => a + Number(n || 0), 0) };
  }
  if (ctx.matrix) {
    return {
      totalClient: ctx.matrix.pricePerDay * nights,
      totalNet: ctx.matrix.basePricePerDay * nights,
    };
  }
  const clientPerDay = Number(vehicle.pricePerDay) || 0;
  const netPerDay = Number(vehicle.basePricePerDay) || 0;
  return { totalClient: clientPerDay * nights, totalNet: netPerDay * nights };
}

const bestTier = <T extends { days: number }>(tiers: T[], nights: number): T | undefined => {
  const applicable = tiers.filter((t) => Number(t.days) <= nights);
  if (!applicable.length) return undefined;
  return applicable.reduce((best, t) => (Number(t.days) > Number(best.days) ? t : best));
};

/** reservationController.resolveBikeStayPricing: campaign → matrix / legacy tiers. */
function bikeStayPrice(vehicle: PricingVehicle, ctx: PricingContext, nights: number): StayPrice {
  const rate = ctx.commissionRate;
  if (ctx.campaign) {
    let client = Number(ctx.campaign.pricePerDay) || 0;
    const cTiers = ctx.campaign.pricingTiers ?? [];
    if (cTiers.length > 0 && nights > 0) {
      const best = bestTier(cTiers, nights);
      if (best) client = Number(best.pricePerDay) || client;
    }
    const net = client / (1 + rate);
    return { totalClient: client * nights, totalNet: net * nights };
  }
  const m = ctx.matrix;
  const tiers = m?.tiers?.length ? m.tiers : (vehicle.pricingTiers ?? []);
  let client = m?.pricePerDay || Number(vehicle.pricePerDay) || 0;
  let net = m?.basePricePerDay || Number(vehicle.basePricePerDay) || 0;
  if (tiers.length > 0 && nights > 0) {
    const best = bestTier(tiers, nights);
    if (best) {
      client = Number(best.pricePerDay) || client;
      net = Number(best.basePricePerDay) || net;
    }
  }
  return { totalClient: client * nights, totalNet: net * nights };
}

/** reservationController.resolveDeliveryFee: matrix → agency → 0, and 0 under free city delivery. */
export function resolveDeliveryFee(rentalType: PricingRequest['rentalType'], ctx: PricingContext): number {
  if (rentalType !== 'delivery') return 0;
  if (ctx.freeCityDelivery) return 0;
  const matrixDelivery = Number(ctx.matrix?.deliveryFee);
  if (Number.isFinite(matrixDelivery) && matrixDelivery > 0) return matrixDelivery;
  const agency = ctx.agencyDelivery;
  const agencyDelivery = agency?.enabled ? Number(agency.price) : 0;
  return Number.isFinite(agencyDelivery) && agencyDelivery > 0 ? agencyDelivery : 0;
}

/** reservationController.resolveReservationPricing (pre-coupon price). */
export function resolveReservationPricing(
  vehicle: PricingVehicle,
  ctx: PricingContext,
  req: PricingRequest,
  nights: number,
): ReservationPricing {
  const globalMinDays = Math.max(1, Number(ctx.platformMinRentalDays) || 1);
  const vehicleMinDays = ctx.matrix?.minRentalDays ?? vehicle.minRentalDays ?? 1;
  const effectiveMinDays = Math.max(globalMinDays, vehicleMinDays);
  if (nights < effectiveMinDays) {
    throw new PricingError(
      `A minimum of ${effectiveMinDays} day(s) is required for this vehicle.`,
      'min_days',
      effectiveMinDays,
    );
  }

  const deliveryFee = resolveDeliveryFee(req.rentalType, ctx);
  const stay = vehicle.type === 'bike' ? bikeStayPrice(vehicle, ctx, nights) : carStayPrice(vehicle, ctx, req, nights);

  let rentalTotal = stay.totalClient;
  const longStayPct = pickLongStayDiscount(nights, ctx.longStay);
  if (longStayPct > 0) rentalTotal = Math.ceil(rentalTotal * (1 - longStayPct / 100));

  const netBaseSum = Number.isFinite(stay.totalNet) ? stay.totalNet : 0;
  return {
    nights,
    effectiveMinDays,
    rentalTotal,
    rentalBeforeDiscount: stay.totalClient,
    longStayPct,
    deliveryFee,
    netBaseSum,
  };
}

/** pricingService.splitReservationCommission: whole MAD, totalPrice = platformFee + agencyRevenue. */
export function splitReservationCommission({
  totalPrice,
  deliveryFee,
  netBaseSum,
}: {
  totalPrice: number;
  deliveryFee: number;
  netBaseSum: number;
}): { totalPrice: number; platformFee: number; agencyRevenue: number } {
  const total = Math.ceil(Number(totalPrice) || 0);
  const safeDeliveryFee = Number.isFinite(deliveryFee) && deliveryFee > 0 ? Math.round(deliveryFee) : 0;
  const rentalClientTotal = Math.max(0, total - safeDeliveryFee);
  const agencyNet = Math.min(rentalClientTotal, Math.round(Number(netBaseSum) || 0));
  const platformFee = Math.max(0, rentalClientTotal - agencyNet);
  return { totalPrice: total, platformFee, agencyRevenue: total - platformFee };
}

/**
 * reservationController.getReservationQuote (MAD, no coupon): validation, billable days,
 * pricing, delivery, split. Throws `PricingError` where the endpoint answers 400.
 */
export function quoteReservation(vehicle: PricingVehicle, ctx: PricingContext, req: PricingRequest): ReservationQuote {
  const nightsBetween = calendarNights(req.startDate, req.returnDate);
  if (!Number.isFinite(nightsBetween)) throw new PricingError('Invalid startDate or returnDate', 'invalid_dates');
  if (nightsBetween <= 0) throw new PricingError('returnDate must be after startDate', 'invalid_dates');
  const pickupTime = req.pickupTime?.trim();
  const returnTime = req.returnTime?.trim();
  if (!TIME.test(pickupTime) || !TIME.test(returnTime)) {
    throw new PricingError('pickupTime and returnTime must be provided in HH:mm (24-hour).', 'invalid_times');
  }
  if (req.rentalType !== 'pickup' && req.rentalType !== 'delivery') {
    throw new PricingError('rentalType must be either "pickup" or "delivery"', 'invalid_rental_type');
  }

  const nights = rentalDaysForPricing(req.startDate, req.returnDate, pickupTime, returnTime);
  const pricing = resolveReservationPricing(vehicle, ctx, { ...req, pickupTime, returnTime }, nights);

  let totalPrice = pricing.rentalTotal;
  if (Number.isFinite(pricing.deliveryFee) && pricing.deliveryFee > 0) {
    totalPrice = fixed2(totalPrice + pricing.deliveryFee);
  }
  const split = splitReservationCommission({
    totalPrice,
    deliveryFee: pricing.deliveryFee,
    netBaseSum: pricing.netBaseSum,
  });
  return { ...pricing, ...split };
}
